import Button from '@/components/core/buttons/Button';
import CopyrightImage from '@/components/core/copyright-image/CopyrightImage';
import Title from '@/components/core/title/Title';
import FilieraArchiveCard from '@/components/shared/cards/FilieraArchiveCard';
import PrimaryCard from '@/components/shared/cards/PrimaryCard';
import CustomSwiper from '@/components/shared/custom-swiper/CustomSwiper';
import { FilieraEntityType } from '@/models/entity-type/EntityType';
import clsx from 'clsx';
import Image from 'next/image';
import React, { memo, useCallback } from 'react';
import { IoPaperPlane } from 'react-icons/io5';
import { Waypoint } from 'react-waypoint';
import { ProdottoType, StageDataType, TipsType } from '../types';
import { Prodotti } from '@/models/interfaces/pages/bodies/detail/itinerario';
import { useTranslation } from 'react-i18next';
import { ID_STELLA_MICHELIN } from '@/constant/constant.utils';

interface StageCardProps {
  transition?: string;
  className?: string;
  greaterThanLg?: boolean;
  headerHeight?: number;
  stage?: StageDataType;
  expanded?: boolean;
  selectedItem?: ProdottoType | null;
  isMapExpanded?: boolean;
  tipsData: {
    cantineEFrantoi: Prodotti[];
    doveMangiare: Prodotti[];
    cosaVedere: Prodotti[];
    eventiData: any[];
  };
  mapOffsetHeight: number;
  currentCardId: number | null;
  cardId: number;
  onWaypoint?: () => void;
  onGoto: (id: string) => void;
  onUpdatePins: (items: TipsType) => void;
  onSelectItemHandler: (item: Prodotti | null, cardId: number) => void;
}

const StageCard = ({
  onGoto,
  onSelectItemHandler,
  onUpdatePins,
  onWaypoint,
  tipsData,
  selectedItem,
  transition = 'transition duration-700 ease-in-out',
  className = '',
  greaterThanLg = false,
  headerHeight = 0,
  stage,
  isMapExpanded = true,
  expanded = false,
  mapOffsetHeight,
  currentCardId,
  cardId,
}: StageCardProps) => {
  const { t } = useTranslation();

  const { cantineEFrantoi, cosaVedere, doveMangiare } = tipsData || {};

  const onChangeSelectedCard = useCallback((ref: React.RefObject<HTMLAnchorElement>) => {
    function isElementInVerticalViewport(el: HTMLElement) {
      const rect = el.getBoundingClientRect();
      return rect.top >= 80 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
    }

    if (ref.current && !isElementInVerticalViewport(ref.current)) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 500);
    }
  }, []);

  const handleSelectTip = useCallback(
    (item: any, cardId: number) => {
      if (currentCardId !== cardId) {
        onGoto(stage?.id || '');
        onUpdatePins(tipsData);
        onWaypoint?.();
      }

      onSelectItemHandler(item, cardId);
    },
    [currentCardId, onGoto, onSelectItemHandler, onUpdatePins, onWaypoint, stage?.id, tipsData],
  );

  return (
    <div
      className={clsx('itinerary-card relative flex items-start lg:min-h-[120vh]', transition, className)}
      style={{
        paddingTop: greaterThanLg ? headerHeight + 100 : 0,
      }}
    >
      <div
        id={stage?.id}
        className={clsx(
          'w-full overflow-hidden bg-white text-left backdrop-blur-md !duration-700 dark:bg-gray-dark/40',
          transition,
          {
            // 'h-fit rounded-r-xl': expanded,
            // 'h-fit rounded-xl': !expanded,
            'h-fit rounded-xl': true,
          },
        )}
      >
        <div
          className={clsx(
            'relative aspect-video w-full overflow-hidden rounded-xl border-b-[6px] border-primary-500',
            'lg:rounded-none',
          )}
        >
          <Image
            blurDataURL="/placeholder.png"
            placeholder="blur"
            src={stage?.image?.url || '/placeholder.png'}
            alt={stage?.image?.alt || 'placeholder'}
            className="block h-full w-full object-cover"
            fill
          />

          <Waypoint
            onEnter={(args) => {
              if (currentCardId !== cardId || !currentCardId) {
                onGoto(stage?.id || '');
                onUpdatePins(tipsData);
                onWaypoint?.();
              }
            }}
            topOffset={greaterThanLg ? headerHeight + 20 : mapOffsetHeight + 20}
            bottomOffset={headerHeight + 50}
          >
            <div className="absolute bottom-2 left-4 z-10 flex h-fit items-center gap-2">
              <Title heading="h2" sizeBar="md" className="h3 text-white">
                {stage?.day}
              </Title>
            </div>
          </Waypoint>

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent " />
        </div>

        <CopyrightImage title={stage?.image?.copyright ?? ''} className="mt-1 px-2 lg:px-4" />

        <div className="flex flex-col gap-6 pt-4">
          <div>
            <div className="px-itinerary-card mx-auto mb-2 flex w-full items-center justify-between gap-2">
              <h3 className="">{stage?.title ?? ''}</h3>
              <Button
                variant="primary"
                size="sm"
                className="flex !h-fit !w-fit items-center justify-center !text-sm"
                onClick={() => {
                  window.open(
                    'https://www.google.com/maps/dir/?api=1&destination=' +
                      stage?.point?.lat +
                      ',' +
                      stage?.point?.lng,
                    '_blank',
                  );
                }}
              >
                <IoPaperPlane className="mr-2 h-3.5 w-3.5" />
                {t('Portami')}
              </Button>
            </div>
            <div
              id="wrapper-height"
              className={clsx('relative', {
                expanded: expanded,
              })}
            >
              <div className="inside relative w-full">
                <div
                  dangerouslySetInnerHTML={{
                    __html: stage?.description ?? '',
                  }}
                  className={clsx('content-itinerary-card px-itinerary-card', {})}
                />

                <div className="mb-5 mt-10 w-full space-y-8">
                  {/* Cantine e frantoi */}
                  {cantineEFrantoi?.length > 0 ? (
                    <div className="w-full space-y-3">
                      <h2 className="h3 px-itinerary-card">{t('Cantine e frantoi')}</h2>
                      <div className="relative z-10 flex h-32 w-full max-w-full gap-4 lg:h-36">
                        <CustomSwiper
                          slidesPerView={'auto'}
                          classNamesSwiperSlides="swiper-slide-itineray-card"
                          mousewheel={true}
                          navigation={true}
                          selectedIndex={cantineEFrantoi?.findIndex(
                            (item) => `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`,
                          )}
                        >
                          {cantineEFrantoi?.map((item, index) => {
                            const isSelected =
                              `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`;

                            return (
                              <FilieraArchiveCard
                                item={{
                                  id: item?.id.toString(),
                                  title: item?.title,
                                  type: item?.type,
                                }}
                                url={item?.permalink}
                                preventDefault={isMapExpanded}
                                selected={isSelected}
                                onChangeSelected={onChangeSelectedCard}
                                type={item?.type.toLowerCase() as FilieraEntityType}
                                key={item.id + index.toString()}
                                className="aspect-video h-full"
                                onClick={() => handleSelectTip(item, cardId)}
                              />
                            );
                          })}
                        </CustomSwiper>
                      </div>
                    </div>
                  ) : null}

                  {/* Dove mangiare */}
                  {doveMangiare?.length > 0 ? (
                    <div className="w-full space-y-3">
                      <h2 className="h3 px-itinerary-card">{t('Dove mangiare')}</h2>
                      <div className="relative z-10 flex h-32 w-full max-w-full gap-4 lg:h-36">
                        <CustomSwiper
                          slidesPerView={'auto'}
                          mousewheel={true}
                          navigation={true}
                          classNamesSwiperSlides="swiper-slide-itineray-card"
                          selectedIndex={doveMangiare?.findIndex(
                            (item) => `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`,
                          )}
                        >
                          {doveMangiare?.map((item, index) => {
                            const isSelected =
                              `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`;

                            return (
                              <FilieraArchiveCard
                                item={{
                                  id: item?.id.toString(),
                                  title: item?.title,
                                  type: item?.type,
                                }}
                                url={item?.permalink}
                                preventDefault={isMapExpanded}
                                selected={isSelected}
                                onChangeSelected={onChangeSelectedCard}
                                type={item?.type.toLowerCase() as FilieraEntityType}
                                isStar={item?.category?.id === ID_STELLA_MICHELIN}
                                key={item.id + index.toString()}
                                className="aspect-video h-full"
                                onClick={() => handleSelectTip(item, cardId)}
                              />
                            );
                          })}
                        </CustomSwiper>
                      </div>
                    </div>
                  ) : null}

                  {/* Cosa Vedere */}
                  {cosaVedere?.length > 0 ? (
                    <div className="w-full space-y-3">
                      <h2 className="h3 px-itinerary-card">{t('Cosa vedere')}</h2>
                      <div className="relative z-10 flex h-[278px] w-full max-w-full gap-4 lg:h-[312px]">
                        <CustomSwiper
                          slidesPerView={'auto'}
                          mousewheel={true}
                          navigation={true}
                          classNamesSwiperSlides="swiper-slide-itineray-card"
                          selectedIndex={cosaVedere?.findIndex(
                            (item) => `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`,
                          )}
                        >
                          {cosaVedere?.map((item, index) => {
                            const isSelected =
                              `${cardId}-${item?.id}` === `${currentCardId}-${selectedItem?.id}`;
                            return (
                              <PrimaryCard
                                item={{
                                  id: item?.id.toString(),
                                  title: item?.title,
                                  image: {
                                    url: item?.image?.url,
                                  },
                                  description: item?.excerpt,
                                  permalink: item?.permalink,
                                  url: item?.permalink,
                                }}
                                preventDefault={isMapExpanded}
                                selected={isSelected}
                                onChangeSelected={onChangeSelectedCard}
                                key={item.id + index.toString()}
                                className="place aspect-[9/11] h-full"
                                onClick={() => handleSelectTip(item, cardId)}
                              />
                            );
                          })}
                        </CustomSwiper>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={clsx(
                  'absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent',
                  {
                    hidden: expanded,
                  },
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(StageCard);
